
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { galleryProjects } from '../../../mocks/galleryProjects';

const featured = galleryProjects.filter((p) => p.featured);

export default function FeaturedShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = featured[activeIndex];

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-[#111111] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={active.image}
          alt=""
          className="w-full h-full object-cover object-top opacity-10 transition-all duration-1000 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-[#111111]/80"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" ref={containerRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-8 sm:mb-12 lg:mb-16"
        >
          <span
            className="text-[#00E5FF] text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Wyróżnione Projekty
          </span>
          <div className="h-[2px] w-16 bg-[#00E5FF] mt-3"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Image first on mobile */}
          <motion.div
            key={`img-${active.id}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative overflow-hidden group">
              <div className="w-full h-[250px] sm:h-[350px] lg:h-[500px]">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#00E5FF] via-[#00E5FF]/50 to-transparent"></div>
              <div className="absolute top-3 right-3 sm:top-6 sm:right-6 w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-[#050505]/60 backdrop-blur-md border border-[#00E5FF]/30">
                <span className="text-[#00E5FF] text-sm sm:text-lg font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {String(activeIndex + 1).padStart(2, '0')}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            key={active.id}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <span
              className="text-[#00E5FF]/60 text-xs sm:text-sm tracking-[0.2em] uppercase font-medium"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              {active.category}
            </span>

            <h2
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mt-3 sm:mt-4 mb-4 sm:mb-6 leading-tight"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              {active.title}
            </h2>

            <p
              className="text-[#C0C7CF] text-sm sm:text-base leading-relaxed mb-6 sm:mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {active.description}
            </p>

            <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-10">
              <div className="border border-[#00E5FF]/20 p-3 sm:p-4 bg-[#050505]/50">
                <span className="text-[#00E5FF] text-[8px] sm:text-xs tracking-wider uppercase block mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Rok
                </span>
                <span className="text-white text-sm sm:text-lg font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {active.year}
                </span>
              </div>
              <div className="border border-[#00E5FF]/20 p-3 sm:p-4 bg-[#050505]/50">
                <span className="text-[#00E5FF] text-[8px] sm:text-xs tracking-wider uppercase block mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Masa
                </span>
                <span className="text-white text-sm sm:text-lg font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {active.weight}
                </span>
              </div>
              <div className="border border-[#00E5FF]/20 p-3 sm:p-4 bg-[#050505]/50">
                <span className="text-[#00E5FF] text-[8px] sm:text-xs tracking-wider uppercase block mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Klient
                </span>
                <span className="text-white text-xs sm:text-sm font-semibold leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {active.client}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {featured.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`h-1 transition-all duration-500 cursor-pointer ${
                    idx === activeIndex
                      ? 'w-8 sm:w-12 bg-[#00E5FF]'
                      : 'w-4 sm:w-6 bg-[#C0C7CF]/20 hover:bg-[#C0C7CF]/40'
                  }`}
                  aria-label={`Projekt ${idx + 1}`}
                ></button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
